// ============================================================
// Centralized mini-landing content config (the 10 SEO pages).
// Each entry powers a single MiniLandingPage component instance.
// ============================================================
import { IMAGES } from "./images";

const IMG_POOL = [
    IMAGES.pillowHero,
    IMAGES.pillowProduct,
    IMAGES.pillowSide,
    IMAGES.pillowDetail,
    IMAGES.pillowFinalCta,
];
const pickImg = (i) => IMG_POOL[i % IMG_POOL.length];

export const LANDINGS = [
    {
        slug: "/best-cervical-pillow-2026",
        breadcrumb: "Best Cervical Pillow 2026",
        kicker: "Editorial Round-Up · 2026",
        h1: "Best Cervical Pillow for Neck Support",
        title: "Best Cervical Pillow for Neck Support (2026) | Better Rest Report",
        description:
            "An editorial round-up of contour cervical pillows for 2026, looking at silhouette, support, breathable fabric, and the design choices that quietly shape better sleep comfort.",
        image: pickImg(0),
        intro:
            "An editorial round-up of contour cervical pillows for 2026. We look at silhouette, support, fabric, and the small design choices that shape a calmer night.",
        keywords: [
            "best cervical pillow",
            "contour pillow support",
            "pillow guide",
            "neck support pillow",
            "sleep comfort",
        ],
        body: [
            {
                heading: "What makes a cervical pillow feel right",
                paragraphs: [
                    "The phrase 'cervical pillow' is used loosely. In editorial terms, what readers tend to mean is a contour silhouette — one shaped around the natural arc of the head and shoulders rather than sitting uniformly flat. A well-considered cervical pillow is quiet, even, and feels deliberately designed.",
                    "We look at three things: the silhouette, the breathability of the cover, and the considered quality of the fill. None of these are clinical claims — they are the small choices that shape sleep comfort.",
                ],
            },
            {
                heading: "What we look for in 2026",
                bullets: [
                    {
                        title: "A considered silhouette",
                        body:
                            "The profile should follow the natural arc of the head and shoulders, with even support and no exaggerated angles.",
                    },
                    {
                        title: "A breathable cover",
                        body:
                            "A soft-blue weave that feels airy against the skin and stays quietly cool through the night.",
                    },
                    {
                        title: "A premium fill",
                        body:
                            "Considered fillings that hold their form over long, ordinary weeks of nightly use.",
                    },
                    {
                        title: "A modern aesthetic",
                        body:
                            "An object that belongs in a calm bedroom — modern, neutral, never loud.",
                    },
                ],
            },
            {
                heading: "Our editorial pick",
                paragraphs: [
                    "The Premium Contour Pillow we cover on the Better Rest Report sits squarely inside this editorial framework — a considered contour silhouette, a breathable soft-blue cover, and premium materials. Quiet, modern, and easy to live with.",
                ],
            },
        ],
        faqs: [
            {
                q: "What is a cervical pillow?",
                a: "It refers to a pillow with a contour silhouette designed around the natural arc of the head and shoulders, intended to offer even, considered support through the night.",
            },
            {
                q: "Are cervical pillows good for everyone?",
                a: "Pillows are personal. A contour silhouette feels considered to many readers, but comfort is subjective — preference matters more than category.",
            },
            {
                q: "What's the best cervical pillow in 2026 editorially?",
                a: "The Premium Contour Pillow covered on the Better Rest Report stands out for its silhouette, breathable cover, and quietly modern aesthetic.",
            },
        ],
    },
    {
        slug: "/flat-pillow-problem",
        breadcrumb: "The Flat Pillow Problem",
        kicker: "Editorial Note · The Issue",
        h1: "The Flat Pillow Problem & Sleep Quality",
        title: "The Flat Pillow Problem & Sleep Quality | Better Rest Report",
        description:
            "An editorial look at the flat pillow problem — why an aging, compressed pillow quietly shapes sleep quality, and what considered contour support brings to the conversation.",
        image: pickImg(1),
        intro:
            "Most pillows quietly stop doing their job long before we notice. An editorial look at why — and what considered contour support brings to the conversation.",
        keywords: [
            "flat pillow",
            "contour pillow support",
            "pillow guide",
            "sleep comfort",
            "better pillow support",
        ],
        body: [
            {
                heading: "What we mean by 'flat'",
                paragraphs: [
                    "A 'flat pillow' is simply one that has lost its considered shape — the fill has packed down, the cover has stretched, and the silhouette no longer follows the natural arc of the head and shoulders. It is a question of comfort, not a clinical condition.",
                ],
            },
            {
                heading: "Why it matters for sleep quality",
                paragraphs: [
                    "A great pillow is something you stop noticing. A flat pillow is something you start noticing — when you fold it in half, stack it on a second one, or turn it over for a cool side that no longer exists. Each of those small frictions chips away at the quiet ritual of rest.",
                ],
            },
            {
                heading: "What a contour silhouette offers",
                paragraphs: [
                    "A contour silhouette is a design choice, not a remedy. The shape traces the natural curve of the head and shoulders, the cover is woven for breathability, and the fill holds its form across long, ordinary weeks.",
                ],
            },
        ],
        faqs: [
            {
                q: "What is meant by 'the flat pillow problem'?",
                a: "It refers to the way pillows quietly compress over time and lose their original loft. It is an editorial observation about everyday pillow wear — not a clinical diagnosis.",
            },
            {
                q: "Is a contour pillow better than a flat one?",
                a: "Whether a contour silhouette feels right is personal. Many readers describe it as a more considered feel, but pillow preference is subjective.",
            },
            {
                q: "How often should you replace a pillow?",
                a: "Many home and bedding writers suggest reviewing a pillow every 18–24 months, depending on fabric, fill, and how it has been cared for.",
            },
        ],
    },
    {
        slug: "/side-sleeper-contour-pillow",
        breadcrumb: "Side Sleeper Contour Pillow",
        kicker: "Editorial Guide · Side Sleep",
        h1: "Best Contour Pillow for Side Sleepers",
        title: "Best Contour Pillow for Side Sleepers | Better Rest Report",
        description:
            "An editorial guide to choosing a contour pillow designed with side-sleep position in mind — silhouette, loft, breathable cover, and the quiet design choices that shape comfort.",
        image: pickImg(2),
        intro:
            "An editorial guide to choosing a contour pillow with side-sleep position in mind. Silhouette, loft, breathable cover — and how each choice quietly shapes comfort.",
        keywords: [
            "side sleeper pillow",
            "contour pillow",
            "side sleep comfort",
            "pillow guide",
            "best pillow for side sleepers",
        ],
        body: [
            {
                heading: "Why silhouette matters more for side sleeping",
                paragraphs: [
                    "Side sleeping creates a small gap between the shoulder and the head. A contour silhouette is designed to sit gently into that gap, offering even support so the head is not asked to balance on a compressed fold of fabric.",
                ],
            },
            {
                heading: "What to look for as a side sleeper",
                bullets: [
                    {
                        title: "A medium-to-full loft",
                        body:
                            "Enough height to fill the space between shoulder and head without elevating it.",
                    },
                    {
                        title: "A considered contour",
                        body:
                            "A profile that follows the natural arc of the head and shoulders.",
                    },
                    {
                        title: "Breathable cover fabric",
                        body:
                            "A soft-blue weave that stays quietly cool through the night, even when lying on one side.",
                    },
                ],
            },
        ],
        faqs: [
            {
                q: "Are contour pillows good for side sleepers?",
                a: "Many readers describe contour silhouettes as a considered fit for side sleeping, because the shape sits gently between the shoulder and head. Personal preference matters most.",
            },
            {
                q: "What loft is best for side sleepers?",
                a: "Editorially, a medium-to-full loft tends to feel right for side sleeping — enough height to fill the shoulder-to-head space without overshooting.",
            },
            {
                q: "Does the Premium Contour Pillow work for side sleepers?",
                a: "The Premium Contour Pillow we cover sits inside the editorial framework most side-sleeping readers describe as comfortable.",
            },
        ],
    },
    {
        slug: "/memory-foam-vs-latex",
        breadcrumb: "Memory Foam vs Latex",
        kicker: "Editorial Comparison",
        h1: "Memory Foam vs Latex Contour Pillows",
        title: "Memory Foam vs Latex Contour Pillows | Better Rest Report",
        description:
            "An editorial side-by-side on memory foam and latex as fills for a contour pillow — feel, breathability, durability, and the quiet differences between them.",
        image: pickImg(3),
        intro:
            "An editorial side-by-side on memory foam and latex as fills for a contour pillow — feel, breathability, durability, and the quiet differences between them.",
        keywords: [
            "memory foam pillow",
            "latex pillow",
            "contour pillow",
            "pillow comparison",
            "pillow guide",
        ],
        body: [
            {
                heading: "Memory foam, in editorial terms",
                paragraphs: [
                    "Memory foam is the fill most readers know best — slow, conforming, and quietly responsive to the shape of the head. At its best it feels like a small, considered embrace. At its worst it can feel warm and slow to recover.",
                ],
            },
            {
                heading: "Latex, in editorial terms",
                paragraphs: [
                    "Latex is springier, cooler, and faster to recover than memory foam. It feels more like a buoyant cushion than a slow conform. Readers who like a quieter, more neutral feel often prefer latex.",
                ],
            },
            {
                heading: "How to choose",
                bullets: [
                    {
                        title: "Choose memory foam if…",
                        body:
                            "you like a slow, conforming feel and don't tend to overheat at night.",
                    },
                    {
                        title: "Choose latex if…",
                        body:
                            "you prefer a springier, cooler, more neutral cushion.",
                    },
                    {
                        title: "Choose a hybrid contour if…",
                        body:
                            "you want a considered silhouette with the breathable, balanced feel of both.",
                    },
                ],
            },
        ],
        faqs: [
            {
                q: "Is memory foam or latex better for a contour pillow?",
                a: "Neither is objectively better. Memory foam conforms slowly; latex is springier and cooler. Comfort is personal — preference matters more than category.",
            },
            {
                q: "Which sleeps cooler, memory foam or latex?",
                a: "Latex generally feels cooler than traditional memory foam because of its more open structure. A breathable cover fabric matters in either case.",
            },
            {
                q: "Which lasts longer?",
                a: "Quality latex tends to retain its shape longer than budget memory foam, but considered memory foam pillows can be very durable too.",
            },
        ],
    },
    {
        slug: "/neck-pain-sleep-guide",
        breadcrumb: "Neck Comfort While Sleeping",
        kicker: "Editorial Guide · Sleep Comfort",
        h1: "How to Improve Neck Comfort While Sleeping",
        title: "How to Improve Neck Comfort While Sleeping | Better Rest Report",
        description:
            "An editorial reader's guide to small, considered choices that shape neck comfort at night — pillow silhouette, loft, materials, and sleep position.",
        image: pickImg(4),
        intro:
            "Small, considered choices shape neck comfort at night. An editorial reader's guide to silhouette, loft, materials, and sleep position. Not medical advice — just a quiet checklist.",
        keywords: [
            "neck comfort while sleeping",
            "pillow guide",
            "contour pillow support",
            "sleep comfort",
            "pillow silhouette",
        ],
        body: [
            {
                heading: "Begin with the pillow's silhouette",
                paragraphs: [
                    "Neck comfort at night begins with the silhouette of the pillow. A considered contour traces the natural arc of the head and shoulders, offering even support so the head is not asked to find a position on a flat surface.",
                ],
            },
            {
                heading: "Check the loft",
                paragraphs: [
                    "Loft — the height of the pillow — should fill the space between the shoulder and the head without elevating the head. A loft that is too low can feel under-supported; a loft that is too high can feel exaggerated.",
                ],
            },
            {
                heading: "Mind the materials",
                paragraphs: [
                    "A breathable cover and a considered fill matter more than they sound. A pillow that breathes feels cooler; a pillow that holds its shape feels more even night after night.",
                ],
            },
            {
                heading: "An editor's reminder",
                paragraphs: [
                    "This is an editorial guide, not professional health advice. If neck discomfort persists, please consult a qualified professional.",
                ],
            },
        ],
        faqs: [
            {
                q: "Can a pillow help with neck comfort?",
                a: "A well-considered contour silhouette can shape a more even, considered feel through the night. For ongoing discomfort, please consult a qualified professional.",
            },
            {
                q: "What pillow loft is best for neck comfort?",
                a: "Editorially, a medium-to-full loft tends to feel right because it fills the shoulder-to-head gap without elevating the head.",
            },
            {
                q: "How long should I try a new pillow?",
                a: "Most editorial writers suggest giving a new pillow at least two to three weeks before deciding whether it feels right.",
            },
        ],
    },
    {
        slug: "/cooling-contour-pillow",
        breadcrumb: "Cooling Contour Pillow",
        kicker: "Editorial Note · Hot Sleepers",
        h1: "Cooling Gel Contour Pillow for Hot Sleepers",
        title: "Cooling Gel Contour Pillow for Hot Sleepers | Better Rest Report",
        description:
            "An editorial look at cooling gel contour pillows — what 'cooling' actually means in a cover fabric and fill, and how to choose one that stays quietly cool through the night.",
        image: pickImg(0),
        intro:
            "What does 'cooling' actually mean in a contour pillow? An editorial look at cover fabric, fill, and the small choices that keep a pillow quietly cool through the night.",
        keywords: [
            "cooling contour pillow",
            "cooling gel pillow",
            "hot sleeper pillow",
            "pillow guide",
            "breathable pillow",
        ],
        body: [
            {
                heading: "What 'cooling' actually means",
                paragraphs: [
                    "In editorial terms, a 'cooling' pillow is one that breathes — a cover woven for airflow and a fill that doesn't trap warmth against the skin. Cooling is not a single feature; it is a small set of design choices.",
                ],
            },
            {
                heading: "What to look for if you sleep warm",
                bullets: [
                    {
                        title: "A breathable cover",
                        body:
                            "A soft-blue weave that lets air move through the fabric instead of trapping it.",
                    },
                    {
                        title: "An open fill structure",
                        body:
                            "Considered fills (latex, open-cell foam, blended fibres) breathe more than dense traditional foam.",
                    },
                    {
                        title: "A modern silhouette",
                        body:
                            "A contour shape that doesn't sit flat against the cheek, allowing airflow around the head.",
                    },
                ],
            },
        ],
        faqs: [
            {
                q: "Are cooling gel contour pillows worth it?",
                a: "A breathable cover and an open fill structure are what most readers describe as 'cooling'. A considered contour silhouette helps airflow around the head.",
            },
            {
                q: "Will a contour pillow sleep hot?",
                a: "Not necessarily. A contour silhouette paired with a breathable cover and an open fill can stay quietly cool through the night.",
            },
            {
                q: "What fabric feels coolest?",
                a: "A soft, airy weave (often described as breathable cotton or technical mesh) tends to feel the coolest against the skin.",
            },
        ],
    },
    {
        slug: "/pillow-height-guide",
        breadcrumb: "Pillow Height Guide",
        kicker: "Editorial Guide · Loft & Height",
        h1: "How to Choose the Right Pillow Loft/Height",
        title: "How to Choose the Right Pillow Loft & Height | Better Rest Report",
        description:
            "An editorial guide to choosing the right pillow loft and height — by sleep position, mattress firmness, and the quiet feel of considered support.",
        image: pickImg(1),
        intro:
            "Loft is the quiet, under-discussed half of a great pillow. An editorial reader's guide to choosing the right height by sleep position and mattress feel.",
        keywords: [
            "pillow height guide",
            "pillow loft",
            "pillow guide",
            "contour pillow loft",
            "sleep comfort",
        ],
        body: [
            {
                heading: "What 'loft' means",
                paragraphs: [
                    "Loft is the height of the pillow when uncompressed — the difference between something that sits gently under the head and something that lifts it. Loft is rarely talked about, and yet it shapes the entire feel of a pillow.",
                ],
            },
            {
                heading: "Loft by sleep position",
                bullets: [
                    {
                        title: "Side sleepers",
                        body:
                            "A medium-to-full loft tends to feel considered, filling the space between shoulder and head.",
                    },
                    {
                        title: "Back sleepers",
                        body:
                            "A medium loft is often described as the most even feel — supportive without elevation.",
                    },
                    {
                        title: "Stomach sleepers",
                        body:
                            "A low loft feels most natural, keeping the head close to the mattress line.",
                    },
                ],
            },
            {
                heading: "Mind the mattress too",
                paragraphs: [
                    "A softer mattress lets the shoulder sink, which makes a slightly lower loft feel right. A firmer mattress holds the shoulder higher, which usually wants a bit more loft to feel even.",
                ],
            },
        ],
        faqs: [
            {
                q: "What is pillow loft?",
                a: "Loft is the height of the pillow when uncompressed — essentially how tall it is. It is one of the quietly important variables in pillow comfort.",
            },
            {
                q: "What loft is best for side sleepers?",
                a: "A medium-to-full loft is often described as the most considered fit for side sleeping.",
            },
            {
                q: "Should mattress firmness change pillow loft choice?",
                a: "Yes — a softer mattress usually wants a slightly lower loft; a firmer mattress wants a bit more.",
            },
        ],
    },
    {
        slug: "/ergonomic-sleep-posture",
        breadcrumb: "Ergonomic Sleep Posture",
        kicker: "Editorial Guide · Posture",
        h1: "Ergonomic Posture Tips for Better Rest",
        title: "Ergonomic Posture Tips for Better Rest | Better Rest Report",
        description:
            "An editorial guide to small posture choices that quietly shape better rest — alignment, pillow silhouette, mattress feel, and the rituals of a calm night.",
        image: pickImg(2),
        intro:
            "Posture shapes rest more than it shapes mornings. An editorial guide to small, considered choices — pillow silhouette, alignment, mattress feel — and how they add up.",
        keywords: [
            "ergonomic sleep posture",
            "sleep posture tips",
            "pillow alignment",
            "pillow guide",
            "sleep comfort",
        ],
        body: [
            {
                heading: "Start with alignment",
                paragraphs: [
                    "Considered sleep posture begins with even alignment of the head, shoulders, and spine. A contour pillow is designed around that idea — supporting the head in the same plane as the body rather than tilting it up or letting it drop.",
                ],
            },
            {
                heading: "Small posture choices that quietly help",
                bullets: [
                    {
                        title: "Match loft to position",
                        body:
                            "A side sleeper wants more loft; a stomach sleeper wants less.",
                    },
                    {
                        title: "Use the contour",
                        body:
                            "If your pillow has a contour silhouette, let it sit naturally — don't fold or flip it.",
                    },
                    {
                        title: "Look at the mattress",
                        body:
                            "A mattress that has gone too soft can quietly disrupt alignment even if the pillow is right.",
                    },
                ],
            },
            {
                heading: "An editor's reminder",
                paragraphs: [
                    "This is an editorial guide, not professional health advice. If sleep posture is causing ongoing discomfort, please consult a qualified professional.",
                ],
            },
        ],
        faqs: [
            {
                q: "What is ergonomic sleep posture?",
                a: "It refers to even alignment of the head, shoulders, and spine during sleep. A considered pillow silhouette and matched loft help support that alignment.",
            },
            {
                q: "Does pillow choice affect sleep posture?",
                a: "Yes — pillow loft and silhouette quietly shape how the head sits in relation to the shoulders and spine.",
            },
            {
                q: "Is a contour pillow good for posture?",
                a: "Many readers describe contour silhouettes as supporting a more even feel through the night.",
            },
        ],
    },
    {
        slug: "/contour-pillow-maintenance",
        breadcrumb: "Contour Pillow Maintenance",
        kicker: "Editorial Guide · Care",
        h1: "How to Clean and Maintain a Contour Pillow",
        title: "How to Clean and Maintain a Contour Pillow | Better Rest Report",
        description:
            "An editorial reader's guide to caring for a contour pillow — washing the cover, refreshing the fill, and the small rituals that keep a considered pillow feeling new.",
        image: pickImg(3),
        intro:
            "A considered pillow rewards a little care. An editorial reader's guide to washing the cover, refreshing the fill, and the small rituals that keep a contour pillow feeling new.",
        keywords: [
            "contour pillow care",
            "pillow maintenance",
            "how to clean contour pillow",
            "pillow guide",
            "pillow care tips",
        ],
        body: [
            {
                heading: "Cover care",
                paragraphs: [
                    "Most contour pillow covers are removable and machine-washable on a gentle cycle. Cool water, mild detergent, and air-drying keeps the soft-blue weave looking like itself for longer.",
                ],
            },
            {
                heading: "Fill care",
                paragraphs: [
                    "The fill itself is usually not machine-washable. A weekly air-out — left to breathe near an open window for an hour — refreshes the fabric and keeps the fill feeling lofty.",
                ],
            },
            {
                heading: "Small rituals that quietly help",
                bullets: [
                    {
                        title: "Rotate weekly",
                        body:
                            "Turn the pillow end-over-end once a week to keep the loft even.",
                    },
                    {
                        title: "Use a pillow protector",
                        body:
                            "A thin breathable protector adds a layer of defence against oils and moisture.",
                    },
                    {
                        title: "Review at 18–24 months",
                        body:
                            "Even a quality pillow softens over time. A quiet review at 18–24 months is a sensible rhythm.",
                    },
                ],
            },
        ],
        faqs: [
            {
                q: "Can I wash a contour pillow?",
                a: "Most covers are machine-washable on a gentle cycle. The fill itself is usually not — check the manufacturer's care label.",
            },
            {
                q: "How often should I wash the cover?",
                a: "Editorial guides usually suggest washing pillow covers every 2–4 weeks, depending on use.",
            },
            {
                q: "How long does a contour pillow last?",
                a: "With care, a quality contour pillow tends to feel considered for 18–24 months before a review is sensible.",
            },
        ],
    },
    {
        slug: "/best-pillow-deals-2026",
        breadcrumb: "Best Pillow Deals 2026",
        kicker: "Editorial Round-Up · Offers",
        h1: "Best Pillow Offers and Deals Today",
        title: "Best Pillow Offers and Deals Today | Better Rest Report",
        description:
            "Today's editorial pick of pillow offers — what's worth a look, what to consider before buying, and how to recognise a considered pillow deal.",
        image: pickImg(4),
        intro:
            "Today's editorial pick of pillow offers — what's worth a look, what to consider before buying, and how to recognise a deal on a considered pillow.",
        keywords: [
            "best pillow deals 2026",
            "pillow offers",
            "contour pillow deal",
            "pillow guide",
            "pillow discounts",
        ],
        body: [
            {
                heading: "What makes an offer worth a look",
                paragraphs: [
                    "A good pillow deal is more than a discount — it is a discount on the right pillow. We look at the silhouette, the cover fabric, and the considered quality of the fill before we look at the price.",
                ],
            },
            {
                heading: "Today's editorial pick",
                paragraphs: [
                    "The Premium Contour Pillow we cover on the Better Rest Report is, in our reading, the most considered example of a contour silhouette currently available at this kind of price point. Availability and pricing change — check today's offer for the most current details.",
                ],
            },
        ],
        faqs: [
            {
                q: "What is the best pillow deal in 2026?",
                a: "Editorially, the Premium Contour Pillow we cover on the Better Rest Report is the considered contour silhouette we recommend looking at today.",
            },
            {
                q: "How do you spot a good pillow deal?",
                a: "Look at the pillow first, then the price. A considered silhouette, breathable cover, and quality fill matter more than a percentage off.",
            },
            {
                q: "Where can I check today's offer?",
                a: "Use the 'Check Today's Offer' button on this page to see the current availability and pricing on the Premium Contour Pillow.",
            },
        ],
    },
];

export const LANDING_BY_SLUG = Object.fromEntries(
    LANDINGS.map((l) => [l.slug, l]),
);
